import _ from 'lodash'
import type { Component, BreakpointRange, BaseDataItem } from '@wix/thunderbolt-becky-types'
import type { CompVariantsViewItem, MegaStore } from '@wix/thunderbolt-catharsis'
import {
	ContainerQueryToSelectorObj,
	MediaQueryToSelectorObj,
	SelectorObj,
	CompIdTo,
	SelectorToCssString as CssString,
} from '../shared.types'
import { getTemplateRepeaterIdSelector, getRegularIdSelector } from './selectorsUtils'
import { ComponentCss, CssContext } from '../features/componentCss.types'
import { accumulateCssContext } from './accumulateCssContextUtils'
import type { CssFeature, FeatureName } from '../features/cssFeature.types'
import { DomApplier, SingleComponentCss } from '../features/cssFeatures.types'
import { mapValues } from './mapValues'
import { CatharsisCssResult } from '../types'
import { WithOverrides } from '@wix/thunderbolt-catharsis'

export type IDomApplierProvider = <TFeatureName extends FeatureName>(
	featureName: TFeatureName
) => DomApplier<TFeatureName>

const EMPTY_ARRAY = [] as const
export const THE_DEFAULT_BREAKPOINT = { id: 'default', mediaQuery: 'default' } as const

const isBreakpointRange = (item: BaseDataItem): item is BreakpointRange => item.type === 'BreakpointRange'
export const toMediaQueryWithId = (item: BreakpointRange) => ({ id: item.id, mediaQuery: toMediaQuery(item) })

export const getChildrenAndSlots = (comp: Component): Array<string> => [
	...(comp.components || EMPTY_ARRAY),
	...(comp.slots ? Object.values(comp.slots) : EMPTY_ARRAY),
]

export const toMediaQuery = (item: BreakpointRange) => {
	const min = item.min ? ` and (min-width: ${item.min}px)` : ''
	const max = item.max ? ` and (max-width: ${item.max}px)` : ''
	return `@media screen${min}${max}`
}

const selectorObjToCss = (
	selectorObj: ContainerQueryToSelectorObj | SelectorObj | CssString,
	insideScopedRule: boolean = false
): string =>
	Object.entries(selectorObj)
		.flatMap(([selector, css]) => {
			if (_.isEmpty(css)) {
				return ''
			}
			// In case we are in stylable component
			if (typeof css === 'string') {
				return css
			} else if (!insideScopedRule && selector.startsWith('@container')) {
				// In case we are ContainerQueryToSelectorObj
				return `${selector}{${selectorObjToCss(css, true)}}`
			} else {
				// In case we are selectorObj
				return `${selector}{${Object.entries(css)
					.map(([key, value]) => `${key}:${value};`)
					.join('')}}`
			}
		})
		.join('')

export function createComponentTraversal(
	getCompById: (compId: string) => ComponentCss,
	compCssMappersGetByIdWithOverrides: <T>(id: string) => WithOverrides<T>,
	isMobileView: boolean,
	domApplierProvider: <TFeatureName extends FeatureName>(featureName: TFeatureName) => DomApplier<TFeatureName>,
	structureStoreGetById: (id: string) => Component
) {
	const traverseComponents = (
		comp: Component,
		ancestorBreakpointsOrder: ComponentCss['breakpointsOrder'],
		acc: CompIdTo<SingleComponentCss>,
		cssContext: CssContext
	) => {
		const compId = comp.id
		const { breakpointsOrder = ancestorBreakpointsOrder, compSpecificData, ...compCss } = getCompById(compId)
		// Remove this once repeater is handled in all cases
		if (compCss && compCss.compCssMappers) {
			compCss.compCssMappers.compCssMappers = compCssMappersGetByIdWithOverrides(compId)
		}

		const getParent = () => ({ getParent: cssContext?.getParent, componentId: compId })
		const accumulatedCssContext = accumulateCssContext(
			comp.id,
			{ ...cssContext, getParent, isMobileView },
			compSpecificData
		)

		acc[compId] = getSingleComponentCss(comp, breakpointsOrder, accumulatedCssContext, compCss, domApplierProvider)

		accumulatedCssContext.isInRepeater = accumulatedCssContext.isInRepeater || comp.type === 'RepeaterContainer'

		getChildrenAndSlots(comp).forEach((childId) =>
			traverseComponents(structureStoreGetById(childId), breakpointsOrder, acc, accumulatedCssContext)
		)
		return acc
	}
	return traverseComponents
}

export const getComponentsCss = (
	megaStore: MegaStore,
	pageCompId: string,
	isMobileView: boolean,
	domApplierProvider: IDomApplierProvider
): CompIdTo<SingleComponentCss> => {
	const cssStore = megaStore.getChildStore('componentCss')
	const compCssMappersStore = megaStore.getChildStore('compCssMappers')
	const structureStore = megaStore.getChildStore('structure')

	const traverseComponents = createComponentTraversal(
		(compId) => cssStore.getById(compId),
		(compId) => compCssMappersStore.getByIdWithOverrides(compId),
		isMobileView,
		domApplierProvider,
		(compId) => structureStore.getById(compId)
	)

	return traverseComponents(structureStore.getById(pageCompId), undefined, {}, {})
}

export const getMediaQueriesFromVariants = (variants: CompVariantsViewItem | null | undefined) =>
	_.filter(variants, isBreakpointRange).map(toMediaQueryWithId)

export const getSingleComponentCss = (
	component: Component,
	breakpointsOrder: ComponentCss['breakpointsOrder'],
	cssContext: CssContext,
	compCss: Omit<ComponentCss, 'breakpointsOrder' | 'compSpecificData'>,
	domApplierProvider: IDomApplierProvider
): SingleComponentCss => {
	const compId = component.id
	const idSelector = cssContext.isInRepeater ? getTemplateRepeaterIdSelector(compId) : getRegularIdSelector(compId)

	const declaredMediaQueries = [
		THE_DEFAULT_BREAKPOINT,
		...(breakpointsOrder?.values || EMPTY_ARRAY).map(toMediaQueryWithId),
	]

	const breakpoints = _.uniqBy(
		[
			...declaredMediaQueries,
			// this is because old editorX sites have css on breakpoint that doesn't exist in the breakpointsOrder
			...getMediaQueriesFromVariants(compCss.responsiveLayout?.layoutVariants),
			...getMediaQueriesFromVariants(compCss.variables?.variablesVariants),
		],
		'id'
	)

	return mapValues(compCss, (css, key) => {
		if (!css) {
			return {}
		}

		return breakpoints.reduce<MediaQueryToSelectorObj>((acc, { id, mediaQuery }) => {
			const selectorObj = domApplierProvider(key)(component, idSelector, id, css as any, cssContext)
			if (!_.isEmpty(selectorObj)) {
				acc[mediaQuery] = acc[mediaQuery] ? _.merge({}, acc[mediaQuery], selectorObj) : selectorObj
			}
			return acc
		}, {})
	})
}

export const stringifyMediaQueryToSelectorObj = (
	mediaQueryToSelectorObj: MediaQueryToSelectorObj,
	parentMediaRule: CssFeature<any, any>['parentMediaRule']
) => {
	let css = ''
	for (const mediaQuery in mediaQueryToSelectorObj) {
		const selectorObj = mediaQueryToSelectorObj[mediaQuery]
		const cssString = selectorObjToCss(selectorObj)
		if (cssString) {
			css += mediaQuery === 'default' ? cssString : `${mediaQuery}{${cssString}}`
		}
	}
	return parentMediaRule && css ? `@media (${parentMediaRule}) {${css}}` : css
}

const replaceCompIdWithClassName = (selector: string) => {
	// Capture the groups of:
	// 1. Regular component ID selectors (e.g. #comp-11111 will become .comp-11111)
	// 2. Repeater prefix ID selectors (e.g. [id^="comp-lwxedckp__"] will become .comp-lwxedckp)
	return selector.replace(
		/\[id\^="([^"]+)__"\]|#(\b[^\s{]+)/g,
		(m, repeaterIdGroup, idGroup) => `.${idGroup || repeaterIdGroup}`
	)
}

export const sliceCssByPresets = (componentsCss: CatharsisCssResult, presets: Array<string>) => {
	const presetsCss = _.reduce(presets, (acc, preset) => Object.assign(acc, { [preset]: {} }), {
		default: {},
	} as { default: CatharsisCssResult; [preset: string]: CatharsisCssResult })

	_.forEach(componentsCss, (featuresCss, compId) => {
		_.forEach(featuresCss, (mediaQueryCss, cssFeatureName) => {
			_.forEach(mediaQueryCss, (selectors, mediaQuery) => {
				_.forEach(selectors, (style, selector) => {
					const presetId = presets.find((preset) => selector.includes(preset))
					let target = presetsCss.default
					if (presetId) {
						selector = selector.replace(new RegExp(`(?<=^|,)\\s*\\.${presetId}\\s+`, 'g'), '')
						target = presetsCss[presetId]
					}
					selector = replaceCompIdWithClassName(selector)
					_.set(target, [compId, cssFeatureName, mediaQuery, selector], style)
				})
			})
		})
	})
	return presetsCss
}
